import { dinero, fechaHora, numero, useTraduccion } from '@tallerya/i18n';
import { PanelLateral } from '@tallerya/ui';
import { detallesDe, textoDeAdjunto, type Campos } from '../../componentes/historial/cambios';
import { LineaDeTiempo, type TextoDeEntrada } from '../../componentes/historial/LineaDeTiempo';
import { usePermisos } from '../../permisos/usePermiso';
import { montoDe, useHistorialOrden, useMontosOrden, type CabeceraOrden, type EntradaHistorial } from './datos';

// El panel «Historial» de la ficha (#141), con la línea de tiempo de `190:9042`:
// lo más nuevo arriba, con el punto del color del estado.
//
//   Historial ⓘ
//   ● Anulada
//     Hoy 10:20 · Ana Batres
//     Motivo: el cliente no volvió
//   ● Total fijado a mano
//     Hoy 09:50 · Ana Batres
//     120,00 US$ (antes 100,00 US$) · Descuento al cliente
//   ● Líneas agregadas
//     Hoy 09:30 · Ana Batres
//     Cambio de aceite, Filtro de aceite, Revisar frenos
//
// - **La línea de tiempo es la común** (`componentes/historial/`, #181); lo de acá
//   es cómo se lee una entrada de la orden, que tiene su propia forma.
// - **Ocho clases de entrada**, decididas con Mario: la apertura, los cambios de
//   estado, el total fijado a mano, las líneas quitadas, las líneas agregadas
//   (#198), las fotos agregadas y eliminadas (#195), con su línea si son de una, y
//   los datos editados de la cabecera (#196). Lo demás no sale. La foto que se va
//   con su línea no sale aparte: ya lo dice «Línea quitada».
// - **Las líneas agregadas van juntas** mientras sea la misma persona y no pase
//   nada visible entre medio; eso lo decide el servidor, acá solo se listan.
// - **La cabecera se lee como en las maestras** (`detallesDe`), un renglón por
//   guardado: «Cliente: Ana López → Pablo Aimar». La falla reportada llega sin
//   valor y dice solo «Cambió: falla reportada…»: en la ficha está entera. Bajo la
//   apertura, el cliente, el vehículo y el asignado con que nació (#197).
// - **Los montos los decide el servidor**: sin `order.amount.view` llegan nulos y la
//   entrada del total dice solo que se fijó y por qué.
// - Lo que no sigue al Figma: el Figma dice «Cambios de estado» bajo el título;
//   ahora hay más que estados, y la explicación va tras el ⓘ (#120). Y el Figma
//   lo dibuja como sección abierta al final de la ficha: se abre en un panel a la
//   derecha, y solo entonces se pide (#193).

function textoDe(
  entrada: EntradaHistorial,
  t: ReturnType<typeof useTraduccion>['t'],
  /** Un monto como llega del contrato —`number | string`, un `decimal`— con la moneda del taller. */
  formato: (monto: number | string) => string,
  /** Cómo se lee cada dato de la cabecera, en la apertura y en «Datos editados». */
  campos: Campos,
): TextoDeEntrada {
  const motivo = entrada.reason ? t('admin.ordenes.historial.motivo', { motivo: entrada.reason }) : undefined;
  // El color del estado, el que eligió el taller; lo demás, un punto gris.
  const color = entrada.statusColor ?? undefined;

  switch (entrada.kind) {
    case 'opened':
      return {
        titulo: entrada.statusName ?? t('admin.ordenes.historial.abierta'),
        detalles: [t('admin.ordenes.historial.abierta'), ...detallesDe(entrada.changes, campos, t, true)],
        color,
      };
    case 'updated':
      return { titulo: t('admin.historial.editado'), detalles: detallesDe(entrada.changes, campos, t) };
    case 'statusChanged':
      return { titulo: entrada.statusName ?? t('admin.ordenes.historial.estado'), detalles: [motivo], color };
    case 'lineRemoved':
      return {
        titulo: t('admin.ordenes.historial.lineaQuitada'),
        detalles: [entrada.lineDescription ? `«${entrada.lineDescription}»` : undefined],
      };
    // Las que una persona agregó una tras otra, en el orden en que entraron (#198).
    // Una sin nombre llega sola y sin descripción: es una, no cero.
    case 'linesAdded':
      return {
        titulo: t('admin.ordenes.historial.lineasAgregadas', { n: Math.max(1, entrada.lineDescriptions.length) }),
        detalles: [entrada.lineDescriptions.join(', ') || undefined],
      };
    // Una foto de la orden, o de una línea, y entonces cuál (#195).
    case 'attachmentAdded':
    case 'attachmentRemoved':
      return {
        titulo: textoDeAdjunto(t, entrada.attachmentKind, entrada.kind === 'attachmentAdded'),
        detalles: [
          entrada.lineDescription ? t('admin.ordenes.historial.enLaLinea', { linea: entrada.lineDescription }) : undefined,
        ],
      };
    case 'totalSet': {
      // Qué pasó va siempre arriba, también para quien no ve montos: fijarlo a mano,
      // o devolverlo a la suma de las líneas.
      const titulo = entrada.totalIsOverridden
        ? t('admin.ordenes.historial.totalFijado')
        : t('admin.ordenes.historial.totalALaSuma');
      // Los montos, solo si el servidor los mandó: sin `order.amount.view` llegan nulos.
      // Puede llegar uno solo: un total fijado donde no había —líneas sin precio— no
      // tiene «antes», y uno devuelto a una suma sin precios no tiene «después».
      const a = entrada.totalAmount !== null ? formato(entrada.totalAmount) : undefined;
      const de = entrada.previousTotalAmount !== null ? formato(entrada.previousTotalAmount) : undefined;
      const cifras =
        a && de
          ? t('admin.ordenes.historial.totalDeA', { a, de })
          : (a ?? (de ? t('admin.ordenes.historial.antes', { de }) : undefined));
      // «120,00 US$ (antes 100,00 US$) · Motivo: …», lo que haya; sin nada, sin línea.
      return { titulo, detalles: [[cifras, motivo].filter(Boolean).join(' · ') || undefined] };
    }
  }
}

export function PanelHistorial({
  orden,
  abierto,
  onCerrar,
}: {
  orden: CabeceraOrden;
  abierto: boolean;
  onCerrar: () => void;
}) {
  const { t, idioma } = useTraduccion();
  const { puede } = usePermisos();
  const id = String(orden.id);
  const consulta = useHistorialOrden(id, abierto);
  // Solo para la moneda de los montos del total, que llegan con este mismo permiso.
  const montos = useMontosOrden(id, puede('order.amount.view'));
  const moneda = montos.data?.currencyCode ?? null;
  const formato = (monto: number) => (moneda ? dinero(monto, moneda, idioma) : numero(monto, idioma));

  // Con las etiquetas de la ficha, para que el historial diga lo mismo que ella.
  const campos: Campos = {
    customer_id: { etiqueta: t('admin.ordenes.ficha.cliente'), vacio: t('admin.ordenes.ficha.sinCliente') },
    asset_id: { etiqueta: t('admin.ordenes.ficha.vehiculo'), vacio: t('admin.ordenes.historial.sinVehiculo') },
    assigned_to: { etiqueta: t('admin.ordenes.ficha.asignadoA'), vacio: t('admin.ordenes.sinAsignar') },
    promised_at: {
      etiqueta: t('admin.ordenes.entregaPrometida'),
      valor: (texto) => fechaHora(texto, idioma),
      vacio: t('admin.ordenes.historial.sinFecha'),
    },
    reported_issue: { etiqueta: t('admin.ordenes.fallaReportada') },
  };

  return (
    <PanelLateral abierto={abierto} onCerrar={onCerrar} titulo={t('admin.historial.titulo')} info={t('admin.ordenes.historial.info')}>
      <LineaDeTiempo
        consulta={consulta}
        texto={(entrada) => textoDe(entrada, t, (monto) => formato(montoDe(monto) ?? 0), campos)}
      />
    </PanelLateral>
  );
}
